import {HttpClient, HttpParams, HttpResponse} from '@angular/common/http';
import {Injectable} from '@angular/core';

import {IFaehigkeit} from '../shared/model/faehigkeit.model';
import {map, Observable} from 'rxjs';
import {environment} from '../../environments/environment';
import {PagedResponse} from '../shared/model/request.model';
import {
  ISkillCategory,
  ISkillCategorySaveRequest,
  ISkillCategorySaveResponse,
  ISkillCreate,
  ISkillPatch,
  ISkillUsageConsultant
} from '../shared/model/skill-management.model';

/** Response type interface faehigkeit **/
type EntityResponseType = HttpResponse<IFaehigkeit>;
/** Response type interface faehigkeit array **/
type EntityArrayResponseType = HttpResponse<IFaehigkeit[]>;

@Injectable({providedIn: 'root'})
export class FaehigkeitenService {
  /** Api url **/
  public resourceUrl = environment.apiConfig.uri + 'api/faehigkeiten';

  /** Creates the http client **/
  constructor(protected http: HttpClient) {
  }

  /** API call to create a new skill **/
  create(faehigkeit: IFaehigkeit): Observable<EntityResponseType> {
    return this.http.post<IFaehigkeit>(this.resourceUrl, faehigkeit, {
      observe: 'response'
    });
  }

  /** HTTP call to update a skill **/
  update(faehigkeit: IFaehigkeit): Observable<EntityResponseType> {
    return this.http.put<IFaehigkeit>(
      `${this.resourceUrl}/${faehigkeit.id}`,
      faehigkeit,
      {observe: 'response'}
    );
  }

  query(req?: any, page?: number, size?: number, approved: boolean = true): Observable<PagedResponse<IFaehigkeit>> {
    let params = new HttpParams().set('page', page ? page : 0).set('size', size ? size : 3000);
    if (req) {
      params = params.set('id', req).set('kategorie', req);
    }
    params = params.set('approved', approved.toString());
    return this.http.get<PagedResponse<IFaehigkeit>>(this.resourceUrl, {params})
  }

  getOneById(id: string) {
    return this.http.get<IFaehigkeit>(`${this.resourceUrl}/${id}`)
  }

  /** API call to delete a skill **/
  delete(id: string): Observable<HttpResponse<any>> {
    return this.http.delete<any>(`${this.resourceUrl}/${id}`, {
      observe: 'response'
    });
  }

  /** API call to merge two skills together **/
  merge(bad: string, good: string) {
    return this.http.post(`${this.resourceUrl}/merge/${bad}/${good}`, null, {
      observe: 'response'
    });
  }

  queryAll(): Observable<IFaehigkeit[]> {
    return this.query().pipe(map((page) => page.content ?? []));
  }

  createApproved(skill: ISkillCreate): Observable<IFaehigkeit> {
    return this.http.post<IFaehigkeit>(`${this.resourceUrl}/approved`, skill);
  }

  patch(id: string, patch: ISkillPatch): Observable<unknown> {
    return this.http.put(`${this.resourceUrl}/${id}`, patch);
  }

  /** Task assignments per skill id, read from the category tree the backend already counts. */
  assignmentCounts(): Observable<Map<string, number>> {
    return this.http.get<SkillTreeNode[]>(`${this.resourceUrl}/tree`).pipe(
      map((tree) => {
        const counts = new Map<string, number>();
        for (const category of tree ?? []) {
          for (const skill of category.children ?? []) {
            if (skill.data?.id != null) {
              counts.set(String(skill.data.id), skill.data.uses ?? 0);
            }
          }
        }
        return counts;
      })
    );
  }

  usage(id: string): Observable<ISkillUsageConsultant[]> {
    return this.http.get<ISkillUsageConsultant[]>(`${this.resourceUrl}/${id}/usage`);
  }

  categories(): Observable<ISkillCategory[]> {
    return this.http.get<ISkillCategory[]>(`${this.resourceUrl}/kategorien`);
  }

  saveCategories(request: ISkillCategorySaveRequest): Observable<ISkillCategorySaveResponse> {
    return this.http.put<ISkillCategorySaveResponse>(`${this.resourceUrl}/kategorien`, request);
  }
}

interface SkillTreeNode {
  data?: {id?: string | number; uses?: number};
  children?: SkillTreeNode[];
}
